import React, { ChangeEvent, DragEvent, FC, useRef, useState } from 'react';
import { localized, localizedInterpolation } from 'state/i18n';
import BiButton from '../buttons/bi-button/bi-button';
import styles from './bi-file-uploader.module.scss';

interface Props {
	allowedFileTypes?: BiFileTypes[];
	maxFileSize?: number; // Size in bytes
	labelText?: string;
	buttonText?: string;
	onFileSelect: (file: File | null) => void;
	file: File | null;
}

export enum BiFileTypes {
	PDF = 'application/pdf',
}

const FileTypeRecords: Record<BiFileTypes, string> = {
	[BiFileTypes.PDF]: '.pdf',
};

export const BiFileUploader: FC<Props> = ({
	allowedFileTypes = [],
	maxFileSize = Infinity,
	labelText = localized('DragAndDropFiles') + ' ' + localized('Or'),
	buttonText = localized('ChooseFile'),
	onFileSelect,
	file = null,
}) => {
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const validateFile = (fileToValidate: File): boolean => {
		if (allowedFileTypes.length > 0 && !allowedFileTypes.includes(fileToValidate.type as BiFileTypes)) {
			const allowedFileTypeNames = allowedFileTypes.map(type => FileTypeRecords[type]);

			setError(localizedInterpolation('InvalidFileType', { allowedFileTypes: allowedFileTypeNames.join(', ') }));
			return false;
		}

		if (fileToValidate.size > maxFileSize) {
			setError(
				localizedInterpolation('FileTooLarge', { maxFileSizeInMB: (maxFileSize / 1024 / 1024).toFixed(2) })
			);
			return false;
		}

		setError(null);
		return true;
	};

	const handleFile = (selectedFile: File | undefined): void => {
		if (selectedFile) {
			if (validateFile(selectedFile)) {
				onFileSelect(selectedFile);
			} else {
				onFileSelect(null);
			}
		}
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
		handleFile(e.target.files?.[0]);
	};

	const preventDefaults = (e: DragEvent<HTMLDivElement>): void => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDragEnter = (e: DragEvent<HTMLDivElement>): void => {
		preventDefaults(e);
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
		preventDefaults(e);
		if (e.currentTarget.contains(e.relatedTarget as Node)) {
			return; // Don't update state if the drag is still inside the component
		}
		setIsDragging(false);
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
		preventDefaults(e);
		setIsDragging(false);
		handleFile(e.dataTransfer.files?.[0]);
	};

	const handleBrowseClick = (): void => {
		setError(null);
		fileInputRef.current?.click();
	};

	return (
		<div
			className={`${styles.fileUploader} ${isDragging ? styles.dragging : ''} ${error ? styles.error : ''}`}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDragOver={preventDefaults}
			onDrop={handleDrop}
		>
			<input
				type="file"
				ref={fileInputRef}
				onChange={handleFileChange}
				className={styles.fileInput}
				accept={allowedFileTypes.join(',')}
			/>
			{file ? (
				<div className={styles.fileInfo}>
					<p>{localized('SelectedFile')}:</p>
					<p>
						<strong>{file.name}</strong>
					</p>
					<p>
						{localized('Size')}: {(file.size / 1024).toFixed(2)} KB
					</p>
				</div>
			) : (
				<div>
					<div className={styles.dropzone}>
						{error ? (
							<p className={styles.errorMessage}>{error}</p>
						) : (
							<>
								<p>{labelText}</p>
							</>
						)}
					</div>
				</div>
			)}
			<div className="flex-center-row margin-top-20px">
				<BiButton colorTheme="org-white" containerTheme="slim-with-rounded-corners" onClick={handleBrowseClick}>
					{buttonText}
				</BiButton>
			</div>
		</div>
	);
};
